import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useUserStore } from '@/store/userStore';
import { useAppName } from './useAppName';
import { toast } from 'sonner';
import { checkSuperAdmin } from '@/services/edgeFunctionWrappers/userRolesWrapper';
import i18n from '@/i18n';
import { consumeIntentionalSignOut, ensureFreshSession, isSessionDefinitivelyInvalid } from '@/services/auth/sessionManager';

// ✅ Cache en mémoire pour éviter les appels répétés à checkSuperAdmin
let superAdminCache: { userId: string; result: boolean; timestamp: number } | null = null;
const SUPER_ADMIN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Routes accessibles sans session : la redirection automatique vers /login
 * ne doit jamais les interrompre (formulaire d'essai public, mot de passe…).
 */
const PUBLIC_ROUTES = ['/login', '/set-password', '/forgot-password', '/trial/'];

const isPublicRoute = (): boolean => {
  const path = window.location.pathname;
  return PUBLIC_ROUTES.some((p) => (p.endsWith('/') ? path.startsWith(p) : path === p));
};


const checkSuperAdminCached = async (userId: string): Promise<boolean> => {
  if (superAdminCache && 
      superAdminCache.userId === userId && 
      Date.now() - superAdminCache.timestamp < SUPER_ADMIN_CACHE_TTL) {
    console.log('[Auth] Using cached superAdmin result');
    return superAdminCache.result;
  }
  const result = await checkSuperAdmin(userId);
  superAdminCache = { userId, result, timestamp: Date.now() };
  return result;
};
export function useSupabaseAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { gymId, setGymId, setAvailableGyms, clearAuth } = useUserStore();
  const appName = useAppName();

  useEffect(() => {
    console.log('Setting up auth state listener');
    
    // ✅ Timeout de sécurité pour éviter les blocages infinis
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Auth loading timeout - forcing loading = false');
      setLoading(false);
    }, 10000); // 10 secondes
    
    // Set up auth state listener FIRST to catch any auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event, newSession ? 'User logged in' : 'No session');
        
        // Conserve l'identité de l'objet `user` tant que c'est le même compte :
        // Supabase émet TOKEN_REFRESHED à CHAQUE retour au premier plan (son
        // propre listener visibilitychange), et un objet neuf à chaque fois
        // relançait l'effet d'AuthGuard → écran « Loading… » à la place de la
        // route → tout formulaire ouvert était démonté et perdu.
        const stableSetUser = (nextUser: User | null) =>
          setUser((prev) => (prev && nextUser && prev.id === nextUser.id ? prev : nextUser));

        // ✅ Gérer l'événement TOKEN_REFRESHED
        if (event === 'TOKEN_REFRESHED') {
          console.log('✅ Token refreshed successfully');
          setSession(newSession);
          stableSetUser(newSession?.user ?? null);
          return;
        }

        setSession(newSession);
        stableSetUser(newSession?.user ?? null);
        
        if (!newSession?.user) {
          // Ne JAMAIS rediriger sur un événement « sans session » non confirmé :
          // au réveil le stockage natif peut répondre vide de façon transitoire.
          const intentional = event === 'SIGNED_OUT' && consumeIntentionalSignOut();
          const redirect = () => {
            clearAuth();
            if (!isPublicRoute()) window.location.href = '/login';
          };
          if (intentional) {
            redirect();
          } else {
            // Hors du callback (appeler supabase.auth ici peut bloquer le client).
            setTimeout(async () => {
              if (await isSessionDefinitivelyInvalid()) {
                redirect();
              } else {
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                  setSession(data.session);
                  stableSetUser(data.session.user);
                }
                console.warn('[Auth] Empty session event ignored (not confirmed):', event);
              }
            }, 0);
          }
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    const checkSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log('Initial session check:', currentSession ? 'Session found' : 'No session');
        
        // If session exists, try to refresh it to ensure it's valid
        if (currentSession) {
          // Rafraîchit seulement si le jeton expire bientôt, via le point unique.
          await ensureFreshSession();
        } else if (!isPublicRoute()) {
          // Session vide au démarrage : on confirme avant de conclure.
          const invalid = await isSessionDefinitivelyInvalid();
          if (!invalid) {
            const { data } = await supabase.auth.getSession();
            if (data.session) {
              setSession(data.session);
              setUser(data.session.user);
            }
          }
        }

        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          
          // ✅ Skip sur la page de login - laisser useLoginAuth gérer le flow
          if (window.location.pathname === '/login') {
            setLoading(false);
            return;
          }
          
          // ✅ Vérifier si c'est un super admin
          const isSuperAdmin = await checkSuperAdminCached(currentSession.user.id);

          if (isSuperAdmin) {
            // Super admin: charger le System Gym
            const systemGymId = '00000000-0000-0000-0000-000000000000';
            setAvailableGyms([{
              id: systemGymId,
              name: 'Super Admin Portal',
              isPrimary: true
            }]);
            setGymId(systemGymId);
          } else {
            // Utilisateur normal: charger les gyms accessibles via edge function (filtré par app)
            const { data: gymAccessData, error: gymAccessError } = await supabase.functions.invoke(
              'get-accessible-gyms',
              { body: { user_id: currentSession.user.id, app_name: appName } }
            );

            // Un échec de chargement des salles ne doit JAMAIS déconnecter :
            // on signale l'erreur, la session reste ouverte et l'app réessaie.
            if (gymAccessError) {
              console.error('Error fetching accessible gyms:', gymAccessError);
              toast.error(i18n.t('auth.fetchAccessError', { ns: 'appui' }));
              return;
            }

            if (!gymAccessData?.success) {
              console.error('Gym access unavailable for app:', appName, gymAccessData?.error);
              toast.error(i18n.t('auth.noAppAccess', { ns: 'appui' }));
              return;
            }

            const assignments = gymAccessData.gyms;
            
            if (assignments && assignments.length > 0) {
              const availableGyms = assignments.map((a: any) => ({
                id: a.gym_id,
                name: a.gyms?.name || 'Unknown Gym',
                isPrimary: a.is_primary_gym
              }));
              
              setAvailableGyms(availableGyms);
              
              // ✅ Sélectionner le gym par défaut avec fallback robuste
              const savedGymId = localStorage.getItem('selected_gym_id');
              const savedGym = savedGymId ? availableGyms.find(g => g.id === savedGymId) : null;
              // ✅ Fallback si le gym sauvegardé n'est pas accessible dans cette app
              const defaultGym = savedGym || availableGyms.find(g => g.isPrimary) || availableGyms[0];
              
              if (defaultGym) {
                setGymId(defaultGym.id);
                // ✅ Mettre à jour le localStorage avec un gym valide pour cette app
                localStorage.setItem('selected_gym_id', defaultGym.id);
                console.log('[Auth] Gym selected:', defaultGym.name, 'for app:', appName);
              }
            } else {
              // Liste vide : on informe sans détruire la session.
              console.warn('No gym assignments found for user');
              toast.error(i18n.t('auth.noAccessibleGym', { ns: 'appui' }));
            }
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    return () => {
      console.log('Cleaning up auth subscription');
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [setGymId, setAvailableGyms, clearAuth]);

  // Deferred fetch of gym assignments when user changes
  useEffect(() => {
    if (!user?.id) return;
    setTimeout(async () => {
      try {
        console.log('Fetching gym assignments for user:', user.id);
        
        // Vérifier si c'est un super admin
        const isSuperAdmin = await checkSuperAdminCached(user.id);

        if (isSuperAdmin) {
          // Super admin: charger le System Gym
          const systemGymId = '00000000-0000-0000-0000-000000000000';
          setAvailableGyms([{
            id: systemGymId,
            name: 'Super Admin Portal',
            isPrimary: true
          }]);
          if (!gymId) {
            setGymId(systemGymId);
          }
        } else {
          // Utilisateur normal: charger les gyms accessibles via edge function (filtré par app)
          const { data: gymAccessData, error: gymAccessError } = await supabase.functions.invoke(
            'get-accessible-gyms',
            { body: { user_id: user.id, app_name: appName } }
          );

          // Idem : on garde la session, on signale seulement l'indisponibilité.
          if (gymAccessError) {
            console.error('Error fetching accessible gyms (deferred):', gymAccessError);
            toast.error(i18n.t('auth.fetchAccessError', { ns: 'appui' }));
            return;
          }

          if (!gymAccessData?.success) {
            console.error('Gym access unavailable (deferred):', appName, gymAccessData?.error);
            toast.error(i18n.t('auth.noAppAccess', { ns: 'appui' }));
            return;
          }

          const assignments = gymAccessData.gyms;
          
          if (assignments && assignments.length > 0) {
            const availableGyms = assignments.map((a: any) => ({
              id: a.gym_id,
              name: a.gyms?.name || 'Unknown Gym',
              isPrimary: a.is_primary_gym
            }));
            
            setAvailableGyms(availableGyms);
            
            // ✅ TOUJOURS vérifier que le gymId actuel est valide pour cette app
            const currentGymValid = gymId ? availableGyms.some(g => g.id === gymId) : false;
            
            if (!currentGymValid) {
              const savedGymId = localStorage.getItem('selected_gym_id');
              const savedGym = savedGymId ? availableGyms.find(g => g.id === savedGymId) : null;
              // ✅ Fallback robuste
              const defaultGym = savedGym || availableGyms.find(g => g.isPrimary) || availableGyms[0];
              
              if (defaultGym) {
                setGymId(defaultGym.id);
                localStorage.setItem('selected_gym_id', defaultGym.id);
                console.log('[Auth] Gym corrected to:', defaultGym.name, 'for app:', appName);
              }
            }
          } else {
            console.warn('No gym assignments found for user (deferred)');
            toast.error(i18n.t('auth.noAccessibleGym', { ns: 'appui' }));
          }
        }
      } catch (e) {
        console.error('Unexpected error fetching gym assignments:', e);
      }
    }, 0);
  }, [user?.id, gymId, setGymId, setAvailableGyms]);

  // ✅ Écouter le retour du background pour revalider les gyms (optimisé avec Promise.all)
  useEffect(() => {
    const handleAppResumed = async () => {
      if (!user?.id) return;
      
      console.log('[Auth] App resumed - revalidating gym access for:', appName);
      
      // ✅ Paralléliser les deux edge functions avec Promise.all
      const [isSuperAdmin, gymAccessResult] = await Promise.all([
        checkSuperAdminCached(user.id),
        supabase.functions.invoke('get-accessible-gyms', { 
          body: { user_id: user.id, app_name: appName } 
        })
      ]);
      
      // Super admin: ne pas recharger les gyms
      if (isSuperAdmin) {
        console.log('[Auth] Super admin detected, skipping gym reload');
        return;
      }
      
      const { data: gymAccessData, error } = gymAccessResult;
      
      if (error || !gymAccessData?.success) {
        console.error('[Auth] Error revalidating gyms after resume:', error || gymAccessData?.error);
        return;
      }
      
      if (gymAccessData.gyms?.length > 0) {
        const availableGyms = gymAccessData.gyms.map((a: any) => ({
          id: a.gym_id,
          name: a.gyms?.name || 'Unknown Gym',
          isPrimary: a.is_primary_gym
        }));

        // Ne réécrire le store que si la liste a réellement changé : un set
        // inconditionnel à chaque resume propageait un nouvel état à tous les
        // consommateurs (dont AuthGuard) sans aucune information nouvelle.
        const current = useUserStore.getState().availableGyms;
        const sameList =
          current.length === availableGyms.length &&
          current.every((g, i) =>
            g.id === availableGyms[i].id &&
            g.name === availableGyms[i].name &&
            g.isPrimary === availableGyms[i].isPrimary
          );
        if (!sameList) setAvailableGyms(availableGyms);
        
        // Vérifier si le gymId actuel est toujours valide pour cette app
        const currentGymValid = gymId ? availableGyms.some(g => g.id === gymId) : false;
        
        if (!currentGymValid) {
          const defaultGym = availableGyms.find(g => g.isPrimary) || availableGyms[0];
          if (defaultGym) {
            setGymId(defaultGym.id);
            localStorage.setItem('selected_gym_id', defaultGym.id);
            console.log('[Auth] Gym corrected after resume to:', defaultGym.name, 'for app:', appName);
          }
        }
      }
    };
    
    window.addEventListener('capacitor-app-resumed', handleAppResumed);
    return () => window.removeEventListener('capacitor-app-resumed', handleAppResumed);
  }, [user?.id, gymId, appName, setAvailableGyms, setGymId]);

  return {
    session,
    user,
    loading,
    gymId,
  };
}
